import { ReactNode, useState } from 'react';

import { Box, SxProps, Theme } from '@mui/material';

export type BaseFileUploadProps = Readonly<{
  onFileSelect: (file: File) => void;
  onError?: (error: Error) => void;
  acceptedFileTypes: string[];
  maxFileSize?: number; // in bytes
  children?: ReactNode | ((props: { isLoading: boolean; fileName: string }) => ReactNode);
  className?: string;
  onDragEnter?: (e: React.DragEvent<HTMLElement>) => void;
  onDragLeave?: (e: React.DragEvent<HTMLElement>) => void;
  onDrop?: (e: React.DragEvent<HTMLElement>) => void;
  onDragOver?: (e: React.DragEvent<HTMLElement>) => void;
  containerSx?: SxProps<Theme>;
  inputId?: string;
}>;

export const BaseFileUpload = ({
  onFileSelect,
  onError,
  acceptedFileTypes = ['.pdf', '.docx', '.txt'],
  maxFileSize = 10 * 1024 * 1024, // 10MB default
  children,
  className,
  containerSx,
  inputId = 'file-upload',
}: BaseFileUploadProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [fileName, setFileName] = useState('');

  const validateFile = (file: File): boolean => {
    const fileExtension = '.' + (file.name.split('.').pop() || file.name).toLowerCase();

    if (!acceptedFileTypes.includes(fileExtension)) {
      onError?.(new Error('Invalid file type'));

      return false;
    }

    if (file.size > maxFileSize) {
      onError?.(new Error('File too large'));

      return false;
    }

    return true;
  };

  const processFile = async (file: File) => {
    if (!file) return;

    if (!validateFile(file)) {
      return;
    }

    setIsLoading(true);
    setFileName(file.name);

    try {
      await onFileSelect(file);
    } catch (error) {
      console.error('Error processing file:', error);
      onError?.(error as Error);
      setFileName('');
    } finally {
      setIsLoading(false);
    }
  };

  const preventDefaults = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    preventDefaults(e);
    const file = e.dataTransfer.files[0];

    if (file) {
      processFile(file);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      processFile(file);
    }

    e.target.value = '';
  };

  const handleDragEnter = (e: React.DragEvent<HTMLElement>) => {
    preventDefaults(e);
    e.currentTarget.classList.add('dragover');
  };

  const handleDragLeave = (e: React.DragEvent<HTMLElement>) => {
    preventDefaults(e);
    e.currentTarget.classList.remove('dragover');
  };

  const handleDragOver = (e: React.DragEvent<HTMLElement>) => {
    preventDefaults(e);
  };

  const handleClick = () => {
    const input = document.getElementById(inputId) as HTMLInputElement;

    if (input) {
      input.click();
    }
  };

  const renderContent = () => {
    if (typeof children === 'function') {
      return children({ isLoading, fileName });
    }

    return children;
  };

  return (
    <Box
      className={className}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={(e) => {
        e.currentTarget.classList.remove('dragover');
        handleDrop(e);
      }}
      onClick={handleClick}
      sx={containerSx}
    >
      {renderContent()}
      <input
        type="file"
        onChange={handleFileChange}
        accept={acceptedFileTypes.join(',')}
        style={{ display: 'none' }}
        id={inputId}
      />
    </Box>
  );
};
